import { useField } from 'formik';
import { useState } from 'react';
import useSWR from 'swr';

import { InputWrapper } from '../../components/InputWrapper';
import { Combobox } from '../../components/combobox/Combobox';
import { listSupplierDrivers, type SupplierDriver } from './models';

export type { SupplierDriver };

type ComboValue = SupplierDriver;

const displayDriver = (driver: ComboValue) => {
  return driver.name;
};

export interface ISupplierDriverComboboxProps {
  placeholder?: string;
  isDisabled?: boolean;
  supplierId: number | null;
  value?: ComboValue | null;
  onChange?: (value: ComboValue | null) => void;
  onBlur?: () => void;
  isInvalid?: boolean;
}

export const SupplierDriverCombobox: React.FC<ISupplierDriverComboboxProps> = (props) => {
  const { placeholder = 'Zoek een chauffeur...', isDisabled, supplierId, value, onChange, onBlur, isInvalid } = props;
  const [searchValue, setSearchValue] = useState('');

  const swrKey = supplierId != null ? [`/api/v1/suppliers/${supplierId}/drivers`, searchValue] : null;
  const { data } = useSWR(swrKey, () => listSupplierDrivers(supplierId!, searchValue || undefined));

  const drivers: ComboValue[] = data?.drivers ?? [];

  return (
    <Combobox
      keyName="id"
      placeholder={supplierId == null ? 'Selecteer eerst een vervoerder...' : placeholder}
      onQueryChange={setSearchValue}
      display={displayDriver}
      items={drivers}
      selectedItem={value}
      onSelect={(val) => {
        onChange?.(val);
      }}
      onBlur={onBlur}
      isDisabled={isDisabled || supplierId == null}
      isInvalid={isInvalid}
      isNullable={true}
    />
  );
};

export interface ISupplierDriverComboboxFieldProps {
  name: string;
  labelText?: string;
  placeholder?: string;
  helperText?: string;
  isDisabled?: boolean;
  supplierId: number | null;
}

export const SupplierDriverComboboxField: React.FC<ISupplierDriverComboboxFieldProps> = (props) => {
  const { name, labelText = 'Chauffeur', placeholder, helperText, isDisabled, supplierId } = props;

  const [field, meta, helpers] = useField({ name });
  const selectedItem = field.value ?? null;

  return (
    <InputWrapper labelText={labelText} invalidText={meta.touched ? meta.error : undefined} helperText={helperText}>
      <SupplierDriverCombobox
        placeholder={placeholder}
        value={selectedItem}
        onChange={helpers.setValue}
        onBlur={() => helpers.setTouched(true)}
        isDisabled={isDisabled}
        isInvalid={Boolean(meta.touched && meta.error)}
        supplierId={supplierId}
      />
    </InputWrapper>
  );
};
